import React, { memo, useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Blok } from "../../interfaces/Blok.interface";
import { withBlokWrapper } from "../BlokWrapper/BlokWrapper";
import { Theme } from "../../interfaces/enums/Theme.enum";
import Container from "../Utils/Container/Container";
import classNames from "classnames";

enum TrustPilotWidgetType {
  Carousel = "carousel",
  Dropdown = "dropdown",
  Grid = "grid",
  Horizontal = "horizontal",
  List = "list",
  ListFiltered = "list-filtered",
  MiniCarousel = "mini-carousel",
  Popup = "pop-up",
  Quote = "quote",
  Slider = "slider"
}

interface TrustPilotProps extends Blok {
  template_id: string;
  business_unit_id: string;
  widget_type: TrustPilotWidgetType;
  /*-- 
    we do have widget theme in the storyblok schema, 
    but we give it the id of theme, because this blok 
    doesn't have it's own theme schema item. However, 
    it can be used for blokWrapping to create the same 
    illusion
  --*/
}

interface TrustPilotBlokProps {
  blok: TrustPilotProps;
}

const getDataStyleHeight = (type: TrustPilotWidgetType): string => {
  switch (type) {
    case TrustPilotWidgetType.Carousel:
      return "140px";
    case TrustPilotWidgetType.Dropdown:
      return "30px";
    case TrustPilotWidgetType.Grid:
      return "500px";
    case TrustPilotWidgetType.Horizontal:
      return "50px";
    case TrustPilotWidgetType.List:
      return "400px";
    case TrustPilotWidgetType.ListFiltered:
      return "400px";
    case TrustPilotWidgetType.MiniCarousel:
      return "320px";
    case TrustPilotWidgetType.Popup:
      return "50px";
    case TrustPilotWidgetType.Quote:
      return "300px";
    case TrustPilotWidgetType.Slider:
      return "240px";
  }
};

const TrustPilot: React.FC<TrustPilotBlokProps> = ({ blok }) => {
  const { template_id, business_unit_id, widget_type, theme } = blok;
  const wrapperClasses = classNames('w-full py-7', {
    "min-h-[206px] py-7": widget_type !== TrustPilotWidgetType.Horizontal,
    "pb-1 py-7": widget_type === TrustPilotWidgetType.Horizontal
  });

  const canShowWidget: boolean = useMemo(() => {
    if (template_id && business_unit_id) return true;
    return false;
  }, [template_id, business_unit_id]);

  const trustPilotWidgetId: string = useMemo(() => `trustpilot-widget--${template_id + '-' + business_unit_id}`, [template_id, business_unit_id])

  useEffect(() => {
    if (canShowWidget) {
      const script = document.createElement("script");
      script.type = "text/javascript"
      script.src =
        "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
      script.async = true;
      document.head.appendChild(script);
      const trustPilotWidget: HTMLElement | null = document.getElementById(trustPilotWidgetId);
      if (trustPilotWidget) window?.Trustpilot?.loadFromElement(trustPilotWidget);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, []);

  const dataStyleHeight = useMemo(() => getDataStyleHeight(widget_type), [widget_type]);

  return (
    <>
      {canShowWidget && (
        <Container>     
          <div className={wrapperClasses}>
            {/* TrustBox widget - Carousel */}
            <div
              className="trustpilot-widget" 
              id={trustPilotWidgetId}
              data-locale="en-GB"
              data-template-id={template_id}
              data-businessunit-id={business_unit_id}
              data-style-width="100%"
              data-style-height={dataStyleHeight}
              data-theme={theme}
              data-stars="4,5"
              data-review-languages="en"
            />
            {/* End TrustBox widget */}
          </div>
        </Container>
      )}
    </>
  );
};

export default memo(withBlokWrapper(TrustPilot));
